import { Box, Typography, Container, Paper, SvgIcon, SvgIconProps } from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { OpenGovLogoIcon } from '@opengov/react-capital-assets';

/**
 * Coming Soon component for entitled apps that don't have a module URL yet.
 * Displays a branded message explaining that the application is under development
 * and will be available once migrated to the Public Service Platform infrastructure.
 */
export function ComingSoonPage({
  appName,
  icon,
}: {
  appName: string;
  icon: React.ComponentType<SvgIconProps>;
}) {
  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${capitalDesignTokens.semanticColors.background.secondary} 0%, ${capitalDesignTokens.semanticColors.background.tertiary} 100%)`,
        py: 8,
        px: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            background: capitalDesignTokens.semanticColors.background.primary,
          }}
        >
          {/* Logo Header */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: capitalDesignTokens.semanticColors.foreground.action,
            }}
          >
            <SvgIcon component={icon || OpenGovLogoIcon} inheritViewBox sx={{ fontSize: 32 }} />
            {/* Main Heading */}
            <Typography
              variant="h2"
              component="h1"
              sx={{
                ml: 1,
                mb: 0.5,
                fontWeight: 700,
              }}
            >
              Coming Soon
            </Typography>
          </Box>

          {/* App Name */}
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: capitalDesignTokens.semanticColors.foreground.primary,
              mb: 4,
            }}
          >
            {appName}
          </Typography>

          {/* Divider */}
          <Box
            sx={{
              width: '80px',
              height: '4px',
              backgroundColor: capitalDesignTokens.semanticColors.foreground.action,
              mx: 'auto',
              mb: 4,
              borderRadius: 2,
            }}
          />

          {/* Main Message */}
          <Typography
            variant="body1"
            sx={{
              color: capitalDesignTokens.semanticColors.foreground.secondary,
              mb: 3,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            The Public Service Platform application is still under development. We are actively
            working with OpenGov's suite development teams to migrate their applications to be
            compatible with the Public Service Platform's remote shell loading infrastructure.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: capitalDesignTokens.semanticColors.foreground.secondary,
              mb: 4,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            When the migration is complete, this placeholder page will be replaced with the
            converted application itself. Please check back later for updates.
          </Typography>

          {/* Info Box */}
          <Box
            sx={{
              mt: 5,
              p: 3,
              backgroundColor: capitalDesignTokens.semanticColors.background.infoLight,
              borderRadius: 1,
              borderLeft: '4px solid',
              borderColor: capitalDesignTokens.semanticColors.foreground.action,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: capitalDesignTokens.semanticColors.foreground.secondary,
                fontStyle: 'italic',
              }}
            >
              This application is part of the OpenGov suite and will be available once it has been
              integrated into the Public Service Platform.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
